import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { C } from "./AccountingLayout";

const TYPES = ["asset", "liability", "equity", "revenue", "cogs", "expense"];
const GROUP_LABEL = { asset: "Assets", liability: "Liabilities", equity: "Equity", revenue: "Revenue", cogs: "Cost of Sales", expense: "Expenses" };

export default function ChartOfAccounts() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ code: "", name: "", account_type: "asset", parent_code: "", description: "" });
  const load = () => api.get("/accounts").then(setRows).catch(() => {});
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const g = {}; TYPES.forEach((t) => g[t] = []); rows.forEach((r) => (g[r.account_type] || []).push(r)); return g;
  }, [rows]);

  const save = async () => {
    try { await api.post("/accounts", draft); toast.success("Account added"); setOpen(false); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const toggle = async (a) => {
    try { await api.put(`/accounts/${a.id}`, { active: !a.active }); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="coa-page">
      <div className="flex justify-end">
        <Button onClick={() => setOpen(true)} style={{ background: C.burgundy }} className="text-white" data-testid="new-account-btn"><Plus size={15} className="mr-1" /> New Account</Button>
      </div>
      {TYPES.map((t) => (
        <div key={t} className="rounded-xl bg-white border" style={{ borderColor: C.border }}>
          <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: C.borderSoft }}>
            <h3 className="font-brand font-bold" style={{ color: C.text }}>{GROUP_LABEL[t]}</h3>
            <span className="text-[11px] font-semibold" style={{ color: C.softMuted }}>{grouped[t]?.length || 0} accounts</span>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[10px] uppercase tracking-widest border-b" style={{ color: C.softMuted, borderColor: C.borderSoft }}>
              <th className="py-2 px-5 w-24">Code</th><th className="py-2 px-3">Account</th><th className="py-2 px-3">Description</th><th className="py-2 px-3">Normal</th><th className="py-2 px-5 w-32 text-right">Active</th>
            </tr></thead>
            <tbody>{(grouped[t] || []).map((a) => (
              <tr key={a.id} className="border-b" style={{ borderColor: C.borderSoft }}>
                <td className="py-2 px-5 font-mono text-[12px]" style={{ color: C.text }}>{a.code}</td>
                <td className="py-2 px-3 font-semibold text-[13px]" style={{ color: C.text }}>{a.name}</td>
                <td className="py-2 px-3 text-[12px]" style={{ color: C.muted }}>{a.description || "—"}</td>
                <td className="py-2 px-3 text-[11px] uppercase font-semibold" style={{ color: C.softMuted }}>{a.normal_balance || (["asset", "cogs", "expense"].includes(a.account_type) ? "debit" : "credit")}</td>
                <td className="py-2 px-5 text-right">
                  <button onClick={() => toggle(a)} className="text-[11px] font-bold px-2 py-1 rounded-full" style={{ background: a.active === false ? "#F1E7D3" : C.greenTint, color: a.active === false ? C.muted : C.green }}>{a.active === false ? "Inactive" : "Active"}</button>
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      ))}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-brand">New Account</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Code</Label><Input value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} data-testid="coa-code" /></div>
              <div className="space-y-1"><Label>Type</Label>
                <Select value={draft.account_type} onValueChange={(v) => setDraft({ ...draft, account_type: v })}>
                  <SelectTrigger data-testid="coa-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{GROUP_LABEL[t]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1"><Label>Name</Label><Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="coa-name" /></div>
            <div className="space-y-1"><Label>Description</Label><Input value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="text-white" style={{ background: C.burgundy }} data-testid="coa-save">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
